import { useState, useRef, TouchEvent } from "react";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import UndoIcon from '@mui/icons-material/Undo';

import type { ICalendarDayData } from "~/components/Calendar";
import type { IGetMenuRecipeItem } from "~/providers/interfaces";
import Meals from "~/components/Meals";
import NutritionInfo from "~/components/NutritionInfo";
import DateInput from "~/components/DateInput";
import { addDaysBe, beDateToLocal, dateToBe, getBeDates, getDateRangeTitle, getMondayBe, getNextMonthEnd, getPrevMonth, getShortDateTitle, getWeekDays, parseBeDate } from "~/utils/date";
import { getLocale } from "~/stores/lang";

import "./style.less";
import { trans } from "~/utils/trans";

// sestaveni jidelnicku najednou nejvys na 7 dni
const MAX_GENERATE_DAYS = 7;
const DAYS_IN_WEEK = 7;
// minimalni posun prstem pro zmenu tydne
const SWIPE_MIN_PX = 60;
// vodorovny posun musi byt aspon tolikrat vetsi nez svisly
const SWIPE_AXIS_RATIO = 2;

type TSheetMode = "generate" | "shopping";

interface IMobileWeek {
	data: Array<ICalendarDayData>;
	goalEnergy: number;
	// vraci true, pokud se jidelnicek sestavil (zavre se okno)
	onGenerate: (fromDate: string, toDate: string) => Promise<boolean>;
	onShoppingList: (fromDate: string, toDate: string) => void;
	openRecipe: (recipe: IGetMenuRecipeItem) => void;
	onRegenerated: () => void;
}

interface ISheet {
	mode: TSheetMode;
	from: string;
	to: string;
}

// "Pondělí" / "Monday" - s velkym pocatecnim pismenem (bez text-transform: uppercase, na kterem nebyla videt diakritika)
function capitalize(text: string) {
	return text.charAt(0).toLocaleUpperCase(getLocale()) + text.slice(1);
}

function getDayName(beDate: string) {
	return capitalize(new Intl.DateTimeFormat(getLocale(), { weekday: "long" }).format(parseBeDate(beDate)));
}

function getShortDate(beDate: string) {
	return getShortDateTitle(parseBeDate(beDate));
}

// "20. – 26. 10. 2026" / "20 – 26 October 2026", pres mesic "28. 10. – 3. 11. 2026" / "28 October – 3 November 2026"
function getWeekTitle(monday: string) {
	return getDateRangeTitle(parseBeDate(monday), parseBeDate(addDaysBe(monday, DAYS_IN_WEEK - 1)), undefined, true);
}

// mobilni zobrazeni jidelnicku: tyden se dny pod sebou, den se rozbali primo v seznamu
export default function MobileWeek({
	data,
	goalEnergy,
	onGenerate,
	onShoppingList,
	openRecipe,
	onRegenerated,
} : IMobileWeek) {
	const today = dateToBe(new Date());
	// stejny rozsah jako u kalendare na desktopu (predchozi az pristi mesic)
	const rangeFrom = dateToBe(getPrevMonth(new Date()));
	const rangeTo = dateToBe(getNextMonthEnd(new Date()));
	const [monday, setMonday] = useState(getMondayBe(today));
	const [expanded, setExpanded] = useState(today);
	const [sheet, setSheet] = useState<ISheet | null>(null);
	const [working, setWorking] = useState(false);
	const touchStart = useRef<{ x: number; y: number } | null>(null);
	const weekDayNames = getWeekDays().map(capitalize);

	const weekDays = getBeDates(monday, addDaysBe(monday, DAYS_IN_WEEK - 1));
	const canPrev = monday > getMondayBe(rangeFrom);
	const canNext = monday < getMondayBe(rangeTo);

	function getDayData(beDate: string) {
		return data.find(item => item.beDate === beDate && item.hasMeals);
	}

	function changeWeek(direction: number) {
		if ((direction < 0 && !canPrev) || (direction > 0 && !canNext)) {
			return;
		}

		const newMonday = addDaysBe(monday, direction * DAYS_IN_WEEK);
		const newDays = getBeDates(newMonday, addDaysBe(newMonday, DAYS_IN_WEEK - 1));

		setMonday(newMonday);
		// v tydnu s dneskem rozbalime dnesek
		setExpanded(newDays.includes(today) ? today : "");
	}

	function goToday() {
		setMonday(getMondayBe(today));
		setExpanded(today);
	}

	function onTouchStart(event: TouchEvent) {
		touchStart.current = { x: event.touches[0].clientX, y: event.touches[0].clientY };
	}

	function onTouchEnd(event: TouchEvent) {
		if (!touchStart.current) {
			return;
		}

		const dx = event.changedTouches[0].clientX - touchStart.current.x;
		const dy = event.changedTouches[0].clientY - touchStart.current.y;

		touchStart.current = null;

		// vodorovny posun, ne posouvani stranky
		if (Math.abs(dx) > SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy) * SWIPE_AXIS_RATIO) {
			changeWeek(dx < 0 ? 1 : -1);
		}
	}

	// predvyplneni: volne dny zobrazeneho tydne od dneska
	function openGenerate() {
		const futureDays = weekDays.filter(day => day >= today);
		const freeDays = futureDays.filter(day => !getDayData(day));
		const from = freeDays[0] || futureDays[0] || today;
		const to = futureDays.length > 0
			? futureDays[futureDays.length - 1]
			: addDaysBe(today, MAX_GENERATE_DAYS - 1);

		setSheet({ mode: "generate", from, to });
	}

	// predvyplneni: dny zobrazeneho tydne s jidelnickem
	function openShopping() {
		const menuDays = weekDays.filter(day => getDayData(day));

		setSheet({
			mode: "shopping",
			from: menuDays[0] || weekDays[0],
			to: menuDays[menuDays.length - 1] || weekDays[DAYS_IN_WEEK - 1],
		});
	}

	function setSheetRange(position: "from" | "to", beDate: string) {
		setSheet(prev => {
			let { from, to } = prev;

			if (position === "from") {
				from = beDate;
				to = to < from ? from : to;
			} else {
				to = beDate;
				from = from > to ? to : from;
			}

			// sestaveni nejvys na 7 dni - posuneme druhy konec rozsahu
			if (prev.mode === "generate" && getBeDates(from, to).length > MAX_GENERATE_DAYS) {
				if (position === "from") {
					to = addDaysBe(from, MAX_GENERATE_DAYS - 1);
				} else {
					from = addDaysBe(to, -(MAX_GENERATE_DAYS - 1));
				}
			}

			return { ...prev, from, to };
		});
	}

	async function confirmSheet() {
		if (sheet.mode === "shopping") {
			setSheet(null);
			onShoppingList(sheet.from, sheet.to);

			return;
		}

		setWorking(true);

		const done = await onGenerate(sheet.from, sheet.to);

		setWorking(false);

		if (done) {
			setSheet(null);
			setExpanded(sheet.from);
		}
	}

	const sheetDays = sheet ? getBeDates(sheet.from, sheet.to) : [];
	const replacedDays = sheet?.mode === "generate" ? sheetDays.filter(day => getDayData(day)).length : 0;

	return <div className="mobileWeek" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
		<div className="mobileWeek__header">
			<Button onClick={() => changeWeek(-1)} disabled={!canPrev} aria-label={trans("mobileWeek.prevWeek")}>
				<ArrowBackIosNewIcon className="MyArrow" />
			</Button>
			<div className="mobileWeek__title">
				<span className="mobileWeek__titleText">
					{getWeekTitle(monday)}
					{ !weekDays.includes(today) && <button type="button" className="mobileWeek__today" onClick={goToday} aria-label={trans("mobileWeek.goToday")} title={trans("mobileWeek.goToday")}>
						<UndoIcon />
					</button> }
				</span>
			</div>
			<Button onClick={() => changeWeek(1)} disabled={!canNext} aria-label={trans("mobileWeek.nextWeek")}>
				<ArrowForwardIosIcon className="MyArrow" />
			</Button>
		</div>

		<ul className="mobileWeek__days">
			{ weekDays.map(day => {
				const dayData = getDayData(day);
				const isOpen = expanded === day;
				const dayName = getDayName(day);

				return <li
					key={day}
					className={[
						"mobileWeek__day",
						dayData ? "mobileWeek__day--menu" : "",
						day === today ? "mobileWeek__day--today" : "",
						day < today ? "mobileWeek__day--past" : "",
						isOpen ? "mobileWeek__day--open" : "",
					].join(" ")}
				>
					<button type="button" className="mobileWeek__dayHead" onClick={() => setExpanded(isOpen ? "" : day)} aria-expanded={isOpen}>
						<span className="mobileWeek__dayName">
							{/* vsechny nazvy dnu v jedne bunce gridu - sirka podle nejdelsiho, datum je tak ve vsech radcich pod sebou */}
							<span className="mobileWeek__weekday">
								{ weekDayNames.map(name => <span key={name} aria-hidden={name !== dayName} className={name === dayName ? "mobileWeek__weekdayCurrent" : ""}>{name}</span>)}
							</span>
							<span className="mobileWeek__date">{getShortDate(day)}</span>
							{/* uplynuly den - jidelnicek uz pro nej nejde sestavit ani menit */}
							{ day < today && <span className="mobileWeek__pastNote">{trans("mobileWeek.pastDay")}</span> }
						</span>
						{/* samostatny prvek mezi nazvem dne a kaloriemi, vycentrovany ve volnem miste */}
						<span className="mobileWeek__todayBadge">{ day === today && trans("mobileWeek.today") }</span>
						<span className="mobileWeek__daySummary">
							{ dayData ? `${Math.round(dayData.menuData.energy)} kcal` : day < today ? trans("mobileWeek.cannotGenerate") : trans("mobileWeek.noMenu") }
						</span>
						<ExpandMoreIcon className="mobileWeek__chevron" />
					</button>
					{ isOpen && dayData && <div className="mobileWeek__dayBody">
						<Meals
							list={dayData.menuData.menu}
							date={day}
							allMenus={data.filter(item => item.menuData).map(item => item.menuData)}
							openRecipe={openRecipe}
							onRegenerated={onRegenerated}
							variant="list"
						/>
						<NutritionInfo
							variant="day"
							goalEnergy={goalEnergy}
							energy={dayData.menuData.energy}
							proteins={dayData.menuData.proteins}
							carbohydrates={dayData.menuData.carbohydrates}
							fats={dayData.menuData.fats}
						/>
					</div> }
					{ isOpen && !dayData && <div className="mobileWeek__dayBody mobileWeek__dayEmpty">
						{ day < today
							? trans("mobileWeek.pastDayInfo")
							: trans("mobileWeek.emptyDayInfo") }
					</div> }
				</li>;
			}) }
		</ul>

		{/* lista primo do body - predek .mainContent ma filter (stin) a s nim by position: fixed nebyla vuci oknu */}
		{ createPortal(<div className="mobileWeek__bar">
			<Button className="MyButton" onClick={openGenerate} variant="contained" startIcon={<RestaurantIcon />}>{trans("mobileWeek.generate")}</Button>
			<Button className="MyButton" onClick={openShopping} variant="contained" startIcon={<ShoppingBasketIcon />}>{trans("mobileWeek.shopping")}</Button>
		</div>, document.body) }

		{/* kalendar pro vyber data (flatpickr) se otevira mimo okno, nesmime mu brat fokus */}
		<Drawer
			anchor="bottom"
			open={sheet !== null}
			onClose={() => !working && setSheet(null)}
			disableEnforceFocus={true}
			slotProps={{ paper: { className: "mobileWeek__sheet" } }}
		>
			{ sheet && <>
				<h2>{ sheet.mode === "generate" ? trans("schedule.generateMenu") : trans("schedule.shoppingList") }</h2>
				<div className="mobileWeek__sheetDates">
					<DateInput
						label={trans("common.from")}
						value={beDateToLocal(sheet.from)}
						onChange={(date, dateData) => setSheetRange("from", dateData.be)}
						minDate={parseBeDate(sheet.mode === "generate" ? today : rangeFrom)}
						maxDate={parseBeDate(rangeTo)}
					/>
					<DateInput
						label={trans("common.to")}
						value={beDateToLocal(sheet.to)}
						onChange={(date, dateData) => setSheetRange("to", dateData.be)}
						minDate={parseBeDate(sheet.mode === "generate" ? today : rangeFrom)}
						maxDate={parseBeDate(rangeTo)}
					/>
				</div>
				<p className="mobileWeek__sheetInfo">
					{trans("mobileWeek.daysCount")} <strong>{sheetDays.length}</strong>
					{ sheet.mode === "generate" && ` ${trans("mobileWeek.maxDays", { max: MAX_GENERATE_DAYS })}` }
				</p>
				{ replacedDays > 0 && <p className="mobileWeek__sheetWarning">
					{trans("mobileWeek.replacedDays", { count: replacedDays })}
				</p> }
				<div className="mobileWeek__sheetButtons">
					<Button className="MyButton" onClick={() => setSheet(null)} variant="contained" disabled={working}>{trans("common.cancel")}</Button>
					<Button className="MyButton" onClick={confirmSheet} variant="contained" disabled={working}>
						{ sheet.mode === "generate" ? (working ? trans("mobileWeek.generating") : trans("schedule.generateMenu")) : trans("mobileWeek.showList") }
					</Button>
				</div>
			</> }
		</Drawer>
	</div>;
}
